import {
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult,
} from 'react-query';
import { AxiosResponse } from 'axios';
import {
    withHeaders,
    instance as axiosInstance,
} from 'core/util/axiosInstance';
import { useAlert } from 'core/contexts';
import { commonShowAlert } from 'lib';
import { getDefaultHeaders } from 'core/util/other';

const BASE_URL = '/metadata/datapipeline';

interface BasePipelineData {
    name: string;
    description: string;
    stepIds: string[];
}

type PostDataPipelineProps = BasePipelineData;

interface PutDataPipelineProps extends BasePipelineData {
    id: string;
}

interface CreateDataPipelineProps {
    postData: PostDataPipelineProps;
    token: string;
}

interface UpdateDataPipelineProps {
    putData: PutDataPipelineProps;
    token: string;
}

interface DeleteDataPipelineProps {
    id: string;
    token: string;
}

interface UpdatePipelineTrialRunProps {
    id: string;
    token: string;
    postData: {};
}

export interface CreatePipelineResponse extends PutDataPipelineProps {
    author: string;
    hash: string;
    timestamp: string;
}

export const getPipelineData = async (
    url: string,
    token: string
): Promise<CreatePipelineResponse> => {
    const headers = getDefaultHeaders(token);
    const response = await axiosInstance().get(url, withHeaders(headers));
    return response.data;
};

export const usePipeline = (
    props: DeleteDataPipelineProps
): UseQueryResult<CreatePipelineResponse, unknown> => {
    const { id, token } = props;
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const url = `${BASE_URL}/${id}`;

    return useQuery(['pipeline', id], async () => getPipelineData(url, token), {
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to retrieve pipeline data';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const postDataPipeline = (
    props: CreateDataPipelineProps
): Promise<AxiosResponse> => {
    const { postData, token } = props;
    const url = BASE_URL;

    return axiosInstance().post(
        url,
        postData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useCreatePipeline = (): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    CreateDataPipelineProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postDataPipeline, {
        onSuccess: () => {
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Pipeline Created'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create pipeline';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const putDataPipeline = (
    props: UpdateDataPipelineProps
): Promise<AxiosResponse> => {
    const { putData, token } = props;
    const url = `${BASE_URL}/${putData.id}`;

    return axiosInstance().put(
        url,
        putData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useUpdatePipeline = (): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    UpdateDataPipelineProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(putDataPipeline, {
        onSuccess: () => {
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Pipeline Updated'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to update pipeline';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const deleteDataPipeline = (
    props: DeleteDataPipelineProps
): Promise<AxiosResponse<void, any>> => {
    const { id, token } = props;
    const url = `${BASE_URL}/${id}`;

    return axiosInstance().delete(url, withHeaders(getDefaultHeaders(token)));
};

export const useDeletePipeline = (): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    DeleteDataPipelineProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(
        (props: DeleteDataPipelineProps) => deleteDataPipeline(props),
        {
            onSuccess: () => {
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'success',
                    'Pipeline Deleted'
                );
            },
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ?? 'Unable to delete pipeline';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};

export const postDataPipelineTrialRun = (
    props: UpdatePipelineTrialRunProps
): Promise<AxiosResponse> => {
    const { id, postData, token } = props;
    const url = `${BASE_URL}/${id}/trial-run`;

    return axiosInstance().post(
        url,
        postData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const usePipelineTrialRun = (): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    UpdatePipelineTrialRunProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postDataPipelineTrialRun, {
        onSuccess: () => {
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Trial Run Created'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create trial run';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
